import { useMemo, useState } from "react"
import { Column, Table } from "@shared/primitive-components"
import { EmbeddedMotorVehicle } from "@shared/types"
import { downloadFleetAsCsv } from "@shared/utils/helpers/downloadAsCsv"
import { getAllPages } from "@shared/platform-api/requests/createRequest"
import { unwrap } from "@shared/utils/helpers/unwrap"
import { fetchFleetEntries, FetchFleetEntriesInput } from "../../policies/api/useFetchFleetEntries"
import { PremiumBand } from "../../policies/api/useFindPolicy/types"
import * as make from "./fleetSummaryData"
import { Data } from "./fleetSummaryData"
import { Details } from "../Details"
import { useAuth } from "../../../contexts"

type TableProps = {
  premiumBands: ReadonlyArray<PremiumBand>
}

const FleetSummaryTable = ({ premiumBands }: TableProps) => {
  const data = useMemo(() => make.data(premiumBands), [premiumBands])
  const columns: ReadonlyArray<Column<Data>> = [
    {
      Header: `Vehicle group`,
      accessor: `label`,
    },
    {
      Header: `Gross premium`,
      accessor: `fixedGrossPremium`,
    },
  ]

  return <Table<Readonly<Data>> data={data} columns={columns} showFooter />
}

type Props = {
  holder: string
  premiumBands: ReadonlyArray<PremiumBand>
  text: string
  policyNumber?: string
  policyId?: string
  showAddButton?: boolean
}

export const FleetSummary = ({ holder, premiumBands, text, policyNumber, policyId, showAddButton }: Props) => {
  const actions = []
  const [isLoading, setIsLoading] = useState(false)
  const auth = useAuth()

  if (policyId) {
    actions.push({
      title: isLoading ? `Exporting...` : `Export Fleet Schedule`,
      onClick: async () => {
        setIsLoading(true)
        const fleetEntries = await getAllPages<FetchFleetEntriesInput, EmbeddedMotorVehicle>(
          fetchFleetEntries,
          async () => unwrap(await auth.getToken()),
          {
            policyId,
            limit: 199,
          }
        )
        if (fleetEntries.tag === `ok`) {
          downloadFleetAsCsv(fleetEntries.data, holder, policyNumber)
        }
        setIsLoading(false)
      },
    })
  }

  if (showAddButton) {
    actions.unshift({
      title: `Go to fleet portal`,
      onClick: () => window.open(`https://portal.flockcover.com/policies/${policyId}/fleet`, `_blank`),
    })
  }

  return (
    <Details title="Vehicle breakdown" summary={[text]} actions={actions}>
      {premiumBands.length ? (
        <FleetSummaryTable premiumBands={premiumBands} />
      ) : (
        <>This policy does not have any on cover vehicles</>
      )}
    </Details>
  )
}

FleetSummary.defaultProps = {
  policyNumber: undefined,
  policyId: undefined,
  showAddButton: false,
}
